import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { radius } from "src/design/tokens";

export interface BrandProps {
  variant: "full" | "compact";
  /** Overrides the default link; renders a button instead of an anchor. */
  onClick?: () => void;
  /** Link target. Defaults to the Today page. */
  to?: string;
}

export const BRAND_HOME_PATH = "/grading/today";

const BrandMark = () => (
  <Box
    aria-hidden
    sx={(theme) => ({
      position: "relative",
      flex: "none",
      width: 34,
      height: 24,
      borderRadius: `${radius.mark}px`,
      background: `repeating-linear-gradient(180deg, ${theme.palette.tokens.danger} 0 3.43px, ${theme.palette.tokens.surface} 3.43px 6.86px)`,
      boxShadow: `inset 0 0 0 1px ${theme.palette.tokens.border}`,
    })}
  >
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 0,
        left: 0,
        width: 14,
        height: 14,
        borderRadius: `${radius.mark}px 0 0 0`,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.onTier,
        fontSize: 9,
        lineHeight: "14px",
        textAlign: "center",
      })}
    >
      ★
    </Box>
  </Box>
);

export const Brand = ({
  variant,
  onClick,
  to = BRAND_HOME_PATH,
}: BrandProps) => {
  const { t } = useTranslation();
  const label = `${t("app.testName")}, ${t("nav.today")}`;
  const linkProps = onClick
    ? { component: "button" as const, type: "button" as const, onClick }
    : { component: RouterLink, to };

  return (
    <Box
      {...linkProps}
      aria-label={label}
      sx={(theme) => ({
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        p: 0,
        border: 0,
        background: "none",
        font: "inherit",
        textAlign: "left",
        textDecoration: "none",
        cursor: "pointer",
        color: theme.palette.text.primary,
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.focusRing}`,
          outlineOffset: 2,
        },
      })}
    >
      <BrandMark />
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box
          component="span"
          sx={{
            fontSize: 14,
            fontWeight: 900,
            fontStretch: "125%",
            textTransform: "uppercase",
            letterSpacing: "-.005em",
            whiteSpace: "nowrap",
            lineHeight: 1.15,
          }}
        >
          {t("app.testName")}
        </Box>
        {variant === "full" ? (
          <Box
            component="span"
            sx={(theme) => ({
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: ".14em",
              textTransform: "uppercase",
              color: theme.palette.tokens.textMuted,
              lineHeight: 1.3,
            })}
          >
            {t("app.byline")}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default Brand;
